/**
 * SequencerService - Native Web Audio API Sequencer
 * 
 * Provides precise timing for step sequencing using Web Audio API's clock.
 * Uses look-ahead scheduling for accurate playback without drift.
 */

import type { Voice } from './NativeVoiceFactory';

export type StepChangeCallback = (step: number) => void;

/**
 * Sequencer service for scheduling and playing notes with precise timing
 */
export class SequencerService {
  private state: 'started' | 'paused' | 'stopped' = 'stopped';
  private voices: Voice[] = [];
  private gridState: boolean[][] = [];
  private noteState: (string | null)[][] = [];
  private steps: number = 16;
  private bpm: number = 120;
  private loopEnabled: boolean = true;
  private onStepChange: StepChangeCallback | undefined;
  private currentStep: number = 0;
  
  // Scheduling state
  private audioContext: AudioContext | null = null;
  private schedulerIntervalId: number | null = null;
  private nextStepTime: number = 0;
  private scheduleAheadTime: number = 0.1; // Schedule 100ms ahead
  private lookAheadInterval: number = 25; // Check every 25ms
  private pendingUIUpdates: number[] = []; // Track setTimeout IDs for cleanup

  /**
   * Initialize sequencer with voices and pattern data
   */
  initSequencer(
    voices: Voice[],
    gridState: boolean[][],
    noteState: (string | null)[][],
    steps: number,
    bpm: number,
    onStepChange?: StepChangeCallback
  ): void {
    this.voices = voices;
    this.gridState = gridState;
    this.noteState = noteState;
    this.steps = steps;
    this.bpm = bpm;
    this.onStepChange = onStepChange;
    
    // Get AudioContext from first voice
    if (voices.length > 0 && voices[0]) {
      this.audioContext = voices[0].gainNode.context as AudioContext;
    }
  }

  /**
   * Start playback with precise Web Audio timing
   */
  async start(): Promise<void> {
    if (this.state === 'started') return;
    if (!this.audioContext) {
      console.error('[SequencerService] No AudioContext available');
      return;
    }
    
    // Clean up any pending state from previous playback
    this.cleanupPendingUpdates();
    
    this.state = 'started';
    this.currentStep = 0;
    
    // Initialize timing - start slightly in the future to ensure first note isn't skipped
    // Add a small buffer (50ms) to account for scheduling overhead
    this.nextStepTime = this.audioContext.currentTime + 0.05;
    
    // Run scheduler immediately to schedule first notes
    this.scheduler();
    
    // Start the scheduler interval
    this.schedulerIntervalId = window.setInterval(() => {
      this.scheduler();
    }, this.lookAheadInterval);
  }

  /**
   * Scheduler - looks ahead and schedules notes
   */
  private scheduler(): void {
    if (!this.audioContext || this.state !== 'started') return;
    
    // Schedule all steps that fall within the look-ahead window
    while (this.nextStepTime < this.audioContext.currentTime + this.scheduleAheadTime) {
      this.scheduleStep(this.currentStep, this.nextStepTime);
      this.nextStep();
    }
  }

  /**
   * Schedule a single step to play at a specific time
   */
  private scheduleStep(step: number, time: number): void {
    if (!this.audioContext) return;
    
    // Calculate note duration (16th note)
    const duration = (60 / this.bpm) / 4;
    
    // Trigger notes for active cells in this step
    this.gridState.forEach((row, rowIndex) => {
      if (row[step] && this.voices[rowIndex]) {
        const note = this.noteState[rowIndex]?.[step] || 'C4';
        
        try {
          this.voices[rowIndex]?.trigger(note, time, duration);
        } catch (error) {
          console.error(`Failed to trigger voice ${rowIndex}:`, error);
        }
      }
    });
    
    // Schedule UI update to sync with audio
    const uiDelay = Math.max(0, (time - this.audioContext.currentTime) * 1000);
    const timeoutId = window.setTimeout(() => {
      if (this.onStepChange && this.state === 'started') {
        this.onStepChange(step);
      }
      // Remove this timeout from pending list
      const index = this.pendingUIUpdates.indexOf(timeoutId);
      if (index > -1) {
        this.pendingUIUpdates.splice(index, 1);
      }
    }, uiDelay);
    
    // Track this timeout for cleanup
    this.pendingUIUpdates.push(timeoutId);
  }

  /**
   * Advance to next step
   */
  private nextStep(): void {
    // Calculate step duration in seconds (16th note = 1/4 of a beat)
    const stepDuration = (60 / this.bpm) / 4;
    
    // Advance time
    this.nextStepTime += stepDuration;
    
    // Advance step
    this.currentStep = (this.currentStep + 1) % this.steps;
    
    // Stop if not looping and we've completed a cycle
    if (!this.loopEnabled && this.currentStep === 0) {
      this.stop();
    }
  }

  /**
   * Clean up pending UI update timeouts
   */
  private cleanupPendingUpdates(): void {
    this.pendingUIUpdates.forEach(timeoutId => {
      window.clearTimeout(timeoutId);
    });
    this.pendingUIUpdates = [];
  }

  /**
   * Pause playback
   */
  pause(): void {
    if (this.schedulerIntervalId !== null) {
      window.clearInterval(this.schedulerIntervalId);
      this.schedulerIntervalId = null;
    }
    this.cleanupPendingUpdates();
    this.state = 'paused';
  }

  /**
   * Stop playback and reset position
   */
  stop(): void {
    if (this.schedulerIntervalId !== null) {
      window.clearInterval(this.schedulerIntervalId);
      this.schedulerIntervalId = null;
    }
    this.cleanupPendingUpdates();
    this.state = 'stopped';
    this.currentStep = 0;
    this.nextStepTime = 0;
    
    // Reset UI to step 0
    if (this.onStepChange) {
      this.onStepChange(0);
    }
  }

  /**
   * Set BPM
   */
  setBpm(bpm: number): void {
    const wasPlaying = this.state === 'started';
    this.bpm = bpm;
    
    // Restart if playing to apply new BPM immediately
    if (wasPlaying) {
      this.stop();
      this.start();
    }
  }

  /**
   * Set loop mode
   */
  setLoop(enabled: boolean): void {
    this.loopEnabled = enabled;
  }

  /**
   * Get current state
   */
  getState(): 'started' | 'paused' | 'stopped' {
    return this.state;
  }

  /**
   * Dispose and clean up
   */
  dispose(): void {
    this.stop();
    this.cleanupPendingUpdates();
    this.voices = [];
    this.gridState = [];
    this.noteState = [];
    this.audioContext = null;
  }
}
